import type { Contract, Milestone } from '../types';

export type TimelineContract = Pick<Contract, 'id' | 'contractNumber'> & Partial<Pick<Contract,
  'projectId' | 'contractChainId' | 'contractVersion' | 'versionStatus' | 'replacesContractId'>>;

function chainKey(contract: TimelineContract, contracts: readonly TimelineContract[]): string {
  let current = contract;
  const visited = new Set<string>();
  while ((!current.contractChainId || current.contractChainId === current.id) &&
    current.replacesContractId && !visited.has(current.id)) {
    visited.add(current.id);
    const parentId = current.replacesContractId;
    const parent = contracts.find(candidate => candidate.id === parentId &&
      candidate.projectId === contract.projectId);
    if (!parent) return `${contract.projectId ?? ''}:${parentId}`;
    current = parent;
  }
  return `${contract.projectId ?? ''}:${current.contractChainId ?? current.id}`;
}

/** Operational timelines show the agreement that applies now, not every draft
 * in its replacement chain. Drafts and superseded versions remain in history. */
export function timelineContracts<T extends TimelineContract>(contracts: readonly T[]): T[] {
  const byChain = new Map<string, T>();
  for (const contract of contracts) {
    if ((contract.versionStatus ?? 'current') !== 'current') continue;
    const key = chainKey(contract, contracts);
    const previous = byChain.get(key);
    // Signing sync can refresh the activated version before the old version is
    // reloaded. Never render both as operational agreements during that gap.
    if (!previous || (contract.contractVersion ?? 1) > (previous.contractVersion ?? 1) ||
      ((contract.contractVersion ?? 1) === (previous.contractVersion ?? 1) &&
        contract.contractNumber > previous.contractNumber)) byChain.set(key, contract);
  }
  return [...byChain.values()].sort((left, right) => left.contractNumber - right.contractNumber);
}

/** Historical stages remain available to contract history and previews, but
 * must not drive today's project status, deadlines or milestone actions. */
export function timelineMilestones(contracts: readonly TimelineContract[], milestones: readonly Milestone[]): Milestone[] {
  const currentIds = new Set(timelineContracts(contracts).map(contract => contract.id));
  return milestones.filter(milestone => currentIds.has(milestone.contractId));
}
